/**
 * Image Compression Utility
 * Compresses images to reduce database storage size
 */

/**
 * Compress an image file to specified quality
 * @param {File} file - The image file to compress
 * @param {number} quality - Compression quality (0-1), default 0.2 (20%)
 * @param {number} maxWidth - Maximum width in pixels, default 800
 * @param {number} maxHeight - Maximum height in pixels, default 800
 * @returns {Promise<string>} - Base64 encoded compressed image
 */
export const compressImage = (file, quality = 0.2, maxWidth = 800, maxHeight = 800) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;
          
          if (width > height) {
            width = maxWidth;
            height = width / aspectRatio;
          } else {
            height = maxHeight;
            width = height * aspectRatio;
          }
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        
        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed base64
        // Use 'image/jpeg' for better compression than PNG
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        
        resolve(compressedBase64);
      };
      
      img.onerror = (error) => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = (error) => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Get file size from base64 string in KB
 * @param {string} base64String - Base64 encoded string
 * @returns {number} - Size in KB
 */
export const getBase64Size = (base64String) => {
  // Remove data URL prefix
  const base64 = base64String.split(',')[1] || base64String;
  
  // Calculate size (base64 is ~33% larger than original binary)
  const sizeInBytes = (base64.length * 3) / 4;
  return Math.round(sizeInBytes / 1024); // Convert to KB
};

/**
 * Validate image file type
 * @param {File} file - The file to validate
 * @returns {boolean} - True if valid image type
 */
export const isValidImageType = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  return validTypes.includes(file.type);
};
